import { of, EMPTY, fromEvent, merge } from 'rxjs';
import { switchMap, catchError, tap } from 'rxjs/operators';
import { fetchJson, postJson, deleteEntity } from './http.js';
import { renderTemplate, replaceViewContainer, showError } from './ui.js';
import { populateSubjectSelect } from './subjects.js';
import { populateSchoolGradeSelect } from './school-grades.js';

/**
 * Builds a single matcher row element. The row contains selects for
 * Grundschule class, Gymnasium class, subject and topic, plus a remove button.
 * topics is an array of { id, name }.
 */
function buildMatcherRow(topics) {
    let row = document.createElement('div');
    row.className = 'matcher-row';

    let grundschuleSelect = document.createElement('select');
    grundschuleSelect.className = 'matcher-grundschule';
    grundschuleSelect.innerHTML = '<option value="">Grundschule –</option>';
    populateSchoolGradeSelect(grundschuleSelect, 'grundschule');

    let gymnasiumSelect = document.createElement('select');
    gymnasiumSelect.className = 'matcher-gymnasium';
    gymnasiumSelect.innerHTML = '<option value="">Gymnasium –</option>';
    populateSchoolGradeSelect(gymnasiumSelect, 'gymnasium');

    let subjectSelect = document.createElement('select');
    subjectSelect.className = 'matcher-subject';
    subjectSelect.innerHTML = '<option value="">Fach –</option>';

    let topicSelect = document.createElement('select');
    topicSelect.className = 'matcher-topic';
    topicSelect.innerHTML =
        '<option value="">Thema –</option>' +
        topics
            .map((t) => `<option value="${t.id}">${t.name}</option>`)
            .join('');

    let removeBtn = document.createElement('button');
    removeBtn.type = 'button';
    removeBtn.className = 'remove-matcher-btn';
    removeBtn.textContent = 'Entfernen';

    row.appendChild(grundschuleSelect);
    row.appendChild(gymnasiumSelect);
    row.appendChild(subjectSelect);
    row.appendChild(topicSelect);
    row.appendChild(removeBtn);

    return row;
}

/**
 * Reads the current matcher values from all matcher rows in the given
 * container. Returns an array of matcher objects.
 */
function readMatchers(matchersContainer) {
    let matchers = [];
    for (let row of matchersContainer.querySelectorAll('.matcher-row')) {
        let matcher = {};
        let grundschule = row.querySelector('.matcher-grundschule').value;
        let gymnasium = row.querySelector('.matcher-gymnasium').value;
        let subject = row.querySelector('.matcher-subject').value;
        let topic = row.querySelector('.matcher-topic').value;
        if (grundschule) {
            matcher.grundschule_class = parseInt(grundschule, 10);
        }
        if (gymnasium) {
            matcher.gymnasium_class = parseInt(gymnasium, 10);
        }
        if (subject) {
            matcher.subject_id = parseInt(subject, 10);
        }
        if (topic) {
            matcher.topic_id = parseInt(topic, 10);
        }
        matchers.push(matcher);
    }
    return matchers;
}

/**
 * Renders the list of saved decks for the selected student into the given
 * <ul> element.
 */
function renderDecks(listEl, decks) {
    listEl.innerHTML = '';
    for (let deck of decks) {
        let li = document.createElement('li');
        li.innerHTML =
            `<span class="deck-name">${deck.name}</span>` +
            `<button class="generate-link-btn" data-id="${deck.id}">Link generieren</button>` +
            `<button class="delete-deck-btn" data-id="${deck.id}">Löschen</button>`;
        listEl.appendChild(li);
    }
}

/**
 * Creates and returns the session setup view Observable. Teachers can define
 * card decks for a student, generate one-time deep links, and manage existing
 * decks.
 */
export function createSessionSetupView() {
    return of(undefined).pipe(
        switchMap(() => {
            let el = renderTemplate('session-setup-view');
            replaceViewContainer(el);

            let studentSelect = el.querySelector('.session-student-select');
            let deckNameInput = el.querySelector('.session-deck-name-input');
            let matchersContainer = el.querySelector('.matchers-container');
            let addMatcherBtn = el.querySelector('.add-matcher-btn');
            let saveDeckBtn = el.querySelector('.save-deck-btn');
            let statusEl = el.querySelector('.status');
            let deckListEl = el.querySelector('.decks-list');
            let linkArea = el.querySelector('.session-link-area');
            let linkEl = el.querySelector('.session-link');

            let topics = [];

            // -----------------------------------------------------------------
            // Populate student select and topics list on load
            // -----------------------------------------------------------------

            let load$ = fetchJson('/api/students').pipe(
                switchMap((students) => {
                    for (let student of students) {
                        let option = document.createElement('option');
                        option.value = String(student.id);
                        option.textContent = student.name;
                        studentSelect.appendChild(option);
                    }
                    return fetchJson('/api/topics');
                }),
                tap((fetchedTopics) => {
                    topics = fetchedTopics;
                }),
                catchError((err) => {
                    showError(err);
                    return EMPTY;
                })
            );

            // -----------------------------------------------------------------
            // Reload deck list when student selection changes
            // -----------------------------------------------------------------

            function loadDecks() {
                let studentId = parseInt(studentSelect.value, 10);
                if (!studentId) {
                    deckListEl.innerHTML = '';
                    return EMPTY;
                }
                return fetchJson(
                    `/api/session-decks?studentId=${studentId}`
                ).pipe(
                    tap((decks) => renderDecks(deckListEl, decks)),
                    catchError((err) => {
                        showError(err);
                        return EMPTY;
                    })
                );
            }

            let studentChange$ = fromEvent(studentSelect, 'change').pipe(
                switchMap(() => loadDecks())
            );

            // -----------------------------------------------------------------
            // Add / remove matchers
            // -----------------------------------------------------------------

            let addMatcher$ = fromEvent(addMatcherBtn, 'click').pipe(
                switchMap(() => {
                    let row = buildMatcherRow(topics);
                    matchersContainer.appendChild(row);
                    return populateSubjectSelect(
                        row.querySelector('.matcher-subject')
                    ).pipe(catchError(() => EMPTY));
                })
            );

            let removeMatcher$ = fromEvent(matchersContainer, 'click').pipe(
                switchMap((e) => {
                    let btn = e.target.closest('.remove-matcher-btn');
                    if (!btn) {
                        return EMPTY;
                    }
                    btn.closest('.matcher-row').remove();
                    return EMPTY;
                })
            );

            // -----------------------------------------------------------------
            // Save deck
            // -----------------------------------------------------------------

            let saveDeck$ = fromEvent(saveDeckBtn, 'click').pipe(
                switchMap(() => {
                    let studentId = parseInt(studentSelect.value, 10);
                    let name = deckNameInput.value.trim();
                    if (!studentId) {
                        statusEl.textContent = 'Bitte einen Schüler auswählen.';
                        return EMPTY;
                    }
                    if (!name) {
                        statusEl.textContent =
                            'Bitte einen Namen für das Deck eingeben.';
                        return EMPTY;
                    }
                    let matchers = readMatchers(matchersContainer);
                    statusEl.textContent = 'Wird gespeichert…';
                    return postJson('/api/session-decks', {
                        student_id: studentId,
                        name,
                        matchers,
                    }).pipe(
                        switchMap(() => {
                            deckNameInput.value = '';
                            matchersContainer.innerHTML = '';
                            statusEl.textContent = 'Deck gespeichert.';
                            return loadDecks();
                        }),
                        catchError((err) => {
                            showError(err);
                            return EMPTY;
                        })
                    );
                })
            );

            // -----------------------------------------------------------------
            // Generate link / delete deck (event delegation on deck list)
            // -----------------------------------------------------------------

            let deckListActions$ = fromEvent(deckListEl, 'click').pipe(
                switchMap((e) => {
                    let generateBtn = e.target.closest('.generate-link-btn');
                    if (generateBtn) {
                        let deckId = generateBtn.dataset.id;
                        return postJson(
                            `/api/session-decks/${deckId}/start-token`,
                            {}
                        ).pipe(
                            switchMap((body) => {
                                let link = `${window.location.protocol}//${window.location.host}/session.html?t=${body.token}`;
                                linkEl.textContent = link;
                                linkEl.href = link;
                                linkArea.hidden = false;
                                statusEl.textContent = '';
                                return EMPTY;
                            }),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }
                    let deleteBtn = e.target.closest('.delete-deck-btn');
                    if (deleteBtn) {
                        if (!window.confirm('Deck wirklich löschen?')) {
                            return EMPTY;
                        }
                        let deckId = deleteBtn.dataset.id;
                        return deleteEntity(
                            `/api/session-decks/${deckId}`
                        ).pipe(
                            switchMap(() => loadDecks()),
                            catchError((err) => {
                                showError(err);
                                return EMPTY;
                            })
                        );
                    }
                    return EMPTY;
                })
            );

            return merge(
                load$,
                studentChange$,
                addMatcher$,
                removeMatcher$,
                saveDeck$,
                deckListActions$
            );
        })
    );
}
